import React from 'react';
import { Grid, Typography, Box, CircularProgress, useTheme } from '@mui/material';
import { People, Visibility, ShoppingBag, TrendingUp, TrendingDown } from '@mui/icons-material';
import { AnalyticsSummaryWithTrends } from '../../../../../services/analytics/AnalyticsService';
import PortalCard from '../../../../../components/PortalCard';

interface AnalyticsSummaryCardsProps {
    summary: AnalyticsSummaryWithTrends | null;
    loading: boolean;
}

const AnalyticsSummaryCards: React.FC<AnalyticsSummaryCardsProps> = ({ summary, loading }) => {
    const theme = useTheme();

    const cards = [
        {
            title: 'Unique Visitors',
            value: summary?.uniqueVisitors || 0,
            trend: summary?.trends?.uniqueVisitors,
            icon: <People sx={{ fontSize: 20, color: 'white' }} />,
            iconBgColor: '#4CAF50'
        },
        {
            title: 'Total Visits',
            value: summary?.totalVisits || 0,
            trend: summary?.trends?.totalVisits,
            icon: <Visibility sx={{ fontSize: 20, color: 'white' }} />,
            iconBgColor: '#9C27B0'
        },
        {
            title: 'Page Views',
            value: summary?.totalPageViews || 0,
            trend: summary?.trends?.totalPageViews,
            icon: <Visibility sx={{ fontSize: 20, color: 'white' }} />,
            iconBgColor: '#2196F3'
        },
        {
            title: 'Product Views',
            value: summary?.totalProductViews || 0,
            trend: summary?.trends?.totalProductViews,
            icon: <ShoppingBag sx={{ fontSize: 20, color: 'white' }} />,
            iconBgColor: '#FF9800'
        }
    ];

    return (
        <Grid container spacing={2}>
            {cards.map((card, index) => (
                <Grid item xs={6} key={index}>
                    <PortalCard
                        sx={{
                            p: 2,
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                        }}
                    >
                        {/* Icon on left */}
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: card.iconBgColor,
                                flexShrink: 0,
                            }}
                        >
                            {card.icon}
                        </Box>

                        {/* Content on right */}
                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                            {/* Title */}
                            <Typography
                                variant='body2'
                                color='text.secondary'
                                fontFamily='primaryFont'
                                sx={{
                                    fontSize: '0.7rem',
                                    lineHeight: 1.2,
                                    textAlign: 'left',
                                }}
                            >
                                {card.title}
                            </Typography>

                            {/* Value */}
                            {loading ? (
                                <CircularProgress size={20} />
                            ) : (
                                <>
                                    <Typography 
                                        variant='h5' 
                                        fontWeight='bold' 
                                        fontFamily='primaryFont'
                                        sx={{ 
                                            lineHeight: 1.1,
                                            textAlign: 'left',
                                        }}
                                    >
                                        {card.value.toLocaleString()}
                                    </Typography>

                                    {/* Trend */}
                                    {card.trend !== undefined && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                            }}
                                        >
                                            {card.trend > 0 ? (
                                                <TrendingUp sx={{ fontSize: 12, color: '#4CAF50' }} />
                                            ) : card.trend < 0 ? (
                                                <TrendingDown sx={{ fontSize: 12, color: '#F44336' }} />
                                            ) : null}
                                            <Typography
                                                variant='caption'
                                                sx={{
                                                    fontFamily: 'primaryFont',
                                                    color: card.trend > 0 ? '#4CAF50' : card.trend < 0 ? '#F44336' : theme.palette.text.secondary,
                                                    fontWeight: 600,
                                                    fontSize: '0.65rem',
                                                    lineHeight: 1.2,
                                                    textAlign: 'left',
                                                }}
                                            >
                                                {card.trend > 0 ? '+' : ''}{card.trend.toFixed(0)}%
                                            </Typography>
                                            <Typography
                                                variant='caption'
                                                sx={{
                                                    fontFamily: 'primaryFont',
                                                    color: theme.palette.text.secondary,
                                                    fontSize: '0.6rem',
                                                    lineHeight: 1.2,
                                                    textAlign: 'left',
                                                }}
                                            >
                                                vs prev
                                            </Typography>
                                        </Box>
                                    )}
                                </>
                            )}
                        </Box>
                    </PortalCard>
                </Grid>
            ))}
        </Grid>
    );
};

export default AnalyticsSummaryCards;


